import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";

import RouteScrollToTop from "./components/RouteScrollToTop.tsx";

const Index = lazy(() => import("./pages/Index.tsx"));
const About = lazy(() => import("./pages/About.tsx"));
const Memberships = lazy(() => import("./pages/Memberships.tsx"));
const SquadUp = lazy(() => import("./pages/SquadUp.tsx"));
const Mission = lazy(() => import("./pages/Mission.tsx"));
const TacticalLoadouts = lazy(() => import("./pages/TacticalLoadouts.tsx"));
const FreeTraining = lazy(() => import("./pages/FreeTraining.tsx"));
const DiscordHelp = lazy(() => import("./pages/DiscordHelp.tsx"));
const DiscordSetup = lazy(() => import("./pages/DiscordSetup.tsx"));
const JoinDiscord = lazy(() => import("./pages/JoinDiscord.tsx"));
const Contact = lazy(() => import("./pages/Contact.tsx"));
const Blog = lazy(() => import("./pages/Blog.tsx"));
const BlogArticle = lazy(() => import("./pages/BlogArticle.tsx"));
const GamePage = lazy(() => import("./pages/GamePage.tsx"));
const RanksAccolades = lazy(() => import("./pages/RanksAccolades.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy.tsx"));
const Accolades = lazy(() => import("./pages/Accolades.tsx"));
const ClanDirectory = lazy(() => import("./pages/ClanDirectory.tsx"));
const FindAUnit = lazy(() => import("./pages/FindAUnit.tsx"));
const ClanPage = lazy(() => import("./pages/ClanPage.tsx"));
const MemberProfilePage = lazy(() => import("./pages/MemberProfilePage.tsx"));
const ResetPassword = lazy(() => import("./pages/ResetPassword.tsx"));
const SubmitLog = lazy(() => import("./pages/SubmitLog.tsx"));
const OfficerTools = lazy(() => import("./pages/OfficerTools.tsx"));
const MyProfile = lazy(() => import("./pages/MyProfile.tsx"));
const MySquad = lazy(() => import("./pages/MySquad.tsx"));
const Events = lazy(() => import("./pages/Events.tsx"));
const SquadFinder = lazy(() => import("./pages/SquadFinder.tsx"));
const WeeklyRecap = lazy(() => import("./pages/WeeklyRecap.tsx"));
const Playbook = lazy(() => import("./pages/Playbook.tsx"));
const Summary = lazy(() => import("./pages/Summary.tsx"));
const CommandGroups = lazy(() => import("./pages/CommandGroups.tsx"));
const OperatorProfile = lazy(() => import("./pages/OperatorProfile.tsx"));
const ShareOperatorProfile = lazy(() => import("./pages/ShareOperatorProfile.tsx"));
const ShareMemberProfile = lazy(() => import("./pages/ShareMemberProfile.tsx"));
const Help = lazy(() => import("./pages/Help.tsx"));
const FAQ = lazy(() => import("./pages/FAQ.tsx"));
const Prizes = lazy(() => import("./pages/Prizes.tsx"));
const ClanWars = lazy(() => import("./pages/ClanWars.tsx"));
const HowItWorks = lazy(() => import("./pages/HowItWorks.tsx"));
const FindTeammates = lazy(() => import("./pages/FindTeammates.tsx"));
const SocialOps = lazy(() => import("./pages/SocialOps.tsx"));
const OperatorPath = lazy(() => import("./pages/OperatorPath.tsx"));
const WarTrack = lazy(() => import("./pages/WarTrack.tsx"));
const WarLobby = lazy(() => import("./pages/WarLobby.tsx"));
const RivalryPage = lazy(() => import("./pages/RivalryPage.tsx"));
const Spotlight = lazy(() => import("./pages/Spotlight.tsx"));
const SpotlightWyldeJezter = lazy(() => import("./pages/SpotlightWyldeJezter.tsx"));
const BringYourCrew = lazy(() => import("./pages/BringYourCrew.tsx"));
const ArchetypesIndex = lazy(() => import("./pages/ArchetypesIndex.tsx"));
const ArchetypesSocial = lazy(() => import("./pages/ArchetypesSocial.tsx"));
const ArchetypesOperator = lazy(() => import("./pages/ArchetypesOperator.tsx"));
const ArchetypesWar = lazy(() => import("./pages/ArchetypesWar.tsx"));
const CasualPatrol = lazy(() => import("./pages/CasualPatrol.tsx"));
const StakedPatrol = lazy(() => import("./pages/StakedPatrol.tsx"));
const FireteamPatrol = lazy(() => import("./pages/FireteamPatrol.tsx"));
const WhyTGH = lazy(() => import("./pages/WhyTGH.tsx"));
const MediaKit = lazy(() => import("./pages/MediaKit.tsx"));
const RanksGlossary = lazy(() => import("./pages/RanksGlossary.tsx"));

const queryClient = new QueryClient();

const AnimatedRoutes = () => {
  return (
    <Suspense fallback={null}>
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/homepage" element={<Navigate to="/" replace />} />
        <Route path="/about" element={<About />} />
        <Route path="/memberships" element={<Memberships />} />
        <Route path="/squadup" element={<SquadUp />} />
        <Route path="/squad-up" element={<Navigate to="/squadup" replace />} />
        <Route path="/mission" element={<Mission />} />
        <Route path="/tacticalloadouts" element={<TacticalLoadouts />} />
        <Route path="/courses" element={<TacticalLoadouts />} />
        <Route path="/free-training" element={<FreeTraining />} />
        <Route path="/discord-help" element={<DiscordHelp />} />
        <Route path="/discord-setup" element={<DiscordSetup />} />
        <Route path="/join-discord" element={<JoinDiscord />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogArticle />} />
        <Route path="/games/:game" element={<GamePage />} />
        <Route path="/ranks" element={<RanksAccolades />} />
        <Route path="/leaderboard" element={<Accolades />} />
        <Route path="/clans" element={<ClanDirectory />} />
        <Route path="/find-a-unit" element={<FindAUnit />} />
        <Route path="/clans/:tag" element={<ClanPage />} />
        <Route path="/rivalry/:matchup" element={<RivalryPage />} />
        <Route path="/members/:username" element={<MemberProfilePage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/profile" element={<MyProfile />} />
        <Route path="/my-teams" element={<MySquad />} />
        <Route path="/submit" element={<OfficerTools />} />
        <Route path="/academy" element={<OfficerTools />} />
        <Route path="/officer-tools" element={<OfficerTools />} />
        <Route path="/events" element={<Events />} />
        <Route path="/lfg" element={<SquadFinder />} />
        <Route path="/recap" element={<WeeklyRecap />} />
        <Route path="/playbook" element={<Playbook />} />
        <Route path="/command-groups" element={<CommandGroups />} />
        <Route path="/prizes" element={<Prizes />} />
        <Route path="/about/summary" element={<Summary />} />
        <Route path="/operator-profile" element={<OperatorProfile />} />
        <Route path="/share/operator/:userId" element={<ShareOperatorProfile />} />
        <Route path="/share/member/:username" element={<ShareMemberProfile />} />
        <Route path="/help" element={<Help />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/clan-wars" element={<ClanWars />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/find-teammates" element={<FindTeammates />} />
        <Route path="/chill-squads" element={<SocialOps />} />
        <Route path="/social-ops" element={<Navigate to="/chill-squads" replace />} />
        <Route path="/operator-path" element={<OperatorPath />} />
        <Route path="/war-track" element={<WarTrack />} />
        <Route path="/wars/lobby/:id" element={<WarLobby />} />
        <Route path="/spotlight" element={<Spotlight />} />
        <Route path="/spotlight/wyldejezter" element={<SpotlightWyldeJezter />} />
        <Route path="/bring-your-crew" element={<BringYourCrew />} />
        <Route path="/archetypes" element={<ArchetypesIndex />} />
        <Route path="/archetypes/social" element={<ArchetypesSocial />} />
        <Route path="/archetypes/operator" element={<ArchetypesOperator />} />
        <Route path="/archetypes/war" element={<ArchetypesWar />} />
        <Route path="/casual-patrol" element={<CasualPatrol />} />
        <Route path="/staked-patrol" element={<StakedPatrol />} />
        <Route path="/fireteam-patrol" element={<FireteamPatrol />} />
        <Route path="/why-tgh" element={<WhyTGH />} />
        <Route path="/media" element={<MediaKit />} />
        <Route path="/glossary" element={<RanksGlossary />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Sonner />
        <BrowserRouter>
          <RouteScrollToTop />
          <AnimatedRoutes />
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
